import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PackageSearch, Mail, ArrowLeft, ShieldCheck } from "lucide-react";

import { CONTACT_EMAIL as SUPPORT_EMAIL } from "@/components/public/categories";

export default function Support() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden" data-testid="support-page">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="support-logo-link">
            <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><PackageSearch className="w-5 h-5 text-white" /></div>
            <span className="font-display font-extrabold text-lg tracking-tight text-white">Drivons<span className="text-brand">Market</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher className="text-white hover:bg-white/10" />
            <Link to="/"><Button data-testid="support-back-btn" variant="ghost" className="text-white hover:bg-white/10 rounded-full gap-1.5"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("support.backHome")}</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="absolute -top-24 right-0 w-[500px] h-[500px] bg-brand/20 rounded-full blur-[120px]" />
        <div className="relative max-w-3xl mx-auto px-5 pt-20 pb-10 animate-fadeup">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-xs font-mono uppercase tracking-widest text-brand mb-6">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("support.badge")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black leading-[1.05] tracking-tight">{t("support.title")}</h1>
          <p className="text-slate-300 text-lg mt-6 max-w-xl">{t("support.subtitle")}</p>
        </div>
      </section>

      {/* Contact Support */}
      <section className="max-w-3xl mx-auto px-5 pb-24">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 animate-fadeup" style={{ animationDelay: "0.1s" }} data-testid="support-contact-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-brand/15 text-brand flex items-center justify-center"><Mail className="w-5 h-5" /></div>
            <h2 className="text-lg md:text-lg font-display font-bold">{t("support.contactTitle")}</h2>
          </div>
          <p className="text-slate-300 text-base">{t("support.contactBody")}</p>

          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{t("support.emailLabel")}</div>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="support-email-link"
              className="inline-flex items-center gap-2 text-brand hover:text-brand-dark font-semibold text-lg break-all underline underline-offset-4"
            >
              <Mail className="w-4 h-4 shrink-0" /> {SUPPORT_EMAIL}
            </a>
          </div>

          <div className="mt-6">
            <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-cta">
              <Button className="bg-brand hover:bg-brand-dark rounded-full h-12 px-7 text-base gap-2">
                <Mail className="w-4 h-4" /> {t("support.emailCta")}
              </Button>
            </a>
          </div>

        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-slate-500">
        © {new Date().getFullYear()} Drivons Market · {t("landing.footer")}
      </footer>
    </div>
  );
}
